import React from 'react';
import { preventNonNumericValue } from 'utils/helpers';
import { PercentageIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import styles from 'feature/supplierTabs/pricing/styles.module.scss';
import { Col, Form, FormModal, InputNumber, Row, Select, Tag } from 'components';
import { useSpecialPricingRulesAddEdit } from './useSpecialPricingRulesAddEdit';

const { Item } = Form;

export interface ISpecialPricingRulesAddUpdateModal {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: Record<string, any>;
}

export const SpecialPricingRulesAddUpdateModal = (props: ISpecialPricingRulesAddUpdateModal) => {
  const { modalState, onModalStateChange, dataToEdit } = props;
  const {
    form,
    isLoading,
    disableSaveBtn,
    formattedPricingRuleBrandData,
    onFinish,
    onCancel,
    handleTypeChange,
    type,
    clearFieldValidation,
  } = useSpecialPricingRulesAddEdit({
    modalState,
    onModalStateChange,
    dataToEdit,
  });
  return (
    <FormModal
      form={form}
      title={`${dataToEdit ? 'Update' : 'Add'} Special Pricing Rules`}
      width={680}
      okText={dataToEdit ? 'Save' : 'Add'}
      open={modalState}
      isLoading={isLoading}
      onCancel={onCancel}
      onSubmit={onFinish}
      disableOkBtn={disableSaveBtn}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Item
            name={'brandId'}
            label={'Brand Name*'}
            rules={[{ required: true, message: ValidationErrors.required('Brand Name') }]}
          >
            <Select size={'large'} options={formattedPricingRuleBrandData} />
          </Item>
        </Col>
        <Col span={12}>
          <Item
            name={'markup'}
            label={'Margin'}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);

                  if (isNaN(num)) {
                    return Promise.reject(new Error('Margin must be a valid number.'));
                  }

                  if (num < 0) {
                    return Promise.reject(new Error('Margin must be a positive number.'));
                  }

                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
            />
          </Item>
        </Col>
        <Col span={12}>
          <Item
            name={'discount'}
            label={'Discount'}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = parseFloat(value);

                  if (isNaN(num)) {
                    return Promise.reject(new Error('Discount must be a valid number.'));
                  }

                  if (num < 0) {
                    return Promise.reject(new Error('Discount must be a positive number.'));
                  }

                  if (num >= 100) {
                    return Promise.reject(new Error('Discount must be less than 100.'));
                  }

                  if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    return Promise.reject(new Error('Discount can have up to 2 decimal points.'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber<number>
              controls={false}
              size={'large'}
              onBeforeInput={(event: any) => preventNonNumericValue(event)}
              style={{ width: '100%' }}
              suffix={<PercentageIcon />}
            />
          </Item>
        </Col>
        <Col span={12}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Item
                label={'Lead Time'}
                name={'minLeadTime'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      if (!/^[1-9]\d*$/.test(String(value))) {
                        return Promise.reject(new Error('Minimum lead time must be a positive integer.'));
                      }
                      const maxLeadTime = getFieldValue('maxLeadTime');
                      if (maxLeadTime && value > maxLeadTime) {
                        return Promise.reject(new Error('Minimum lead time must be less than Maximum.'));
                      }
                      clearFieldValidation(form, 'maxLeadTime');
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  placeholder={'Min'}
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item
                label={<span style={{ display: 'none' }}></span>}
                name={'maxLeadTime'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      if (!/^[1-9]\d*$/.test(String(value))) {
                        return Promise.reject(new Error('Maximum lead time must be a positive integer.'));
                      }
                      const minLeadTime = getFieldValue('minLeadTime');
                      if (minLeadTime !== undefined && value < minLeadTime) {
                        return Promise.reject(new Error('Maximum lead time must be more than Minimum.'));
                      }
                      clearFieldValidation(form, 'minLeadTime');
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={
                    <Row className={'gap-4'}>
                      <Tag
                        className={`${styles.tag} ${type === 'DAY' && styles.selected_tag}`}
                        onClick={() => handleTypeChange('DAY')}
                      >
                        Day
                      </Tag>
                      <Tag
                        className={`${styles.tag} ${type === 'WEEK' && styles.selected_tag}`}
                        onClick={() => handleTypeChange('WEEK')}
                      >
                        Week
                      </Tag>
                    </Row>
                  }
                  placeholder={'Max'}
                />
              </Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </FormModal>
  );
};
